import { Skeleton } from '@/components/ui/skeleton';

/* Generic helpers ---------------------------------------------------------- */

export const SkeletonGrid = ({
  count = 6,
  className = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6',
  itemClassName = 'h-72 rounded-2xl',
}: { count?: number; className?: string; itemClassName?: string }) => (
  <div className={className}>
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} className={itemClassName} />
    ))}
  </div>
);

/* Firm card (BrowseFirms / featured grids) -------------------------------- */

export const FirmCardSkeleton = () => (
  <div className="relative bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))]/60 rounded-2xl p-6 border border-border/40 overflow-hidden">
    <div className="flex items-start justify-between mb-4">
      <Skeleton className="w-16 h-16 rounded-xl" />
      <Skeleton className="w-16 h-6 rounded-lg" />
    </div>
    <Skeleton className="h-6 w-3/4 mb-3" />
    <div className="flex items-center gap-2 mb-4 pb-4 border-b border-border/40">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-10" />
    </div>
    <Skeleton className="h-9 w-full rounded-lg mb-4" />
    <Skeleton className="h-11 w-full rounded-xl" />
  </div>
);

export const FirmGridSkeleton = ({ count = 8 }: { count?: number }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {Array.from({ length: count }).map((_, i) => <FirmCardSkeleton key={i} />)}
  </div>
);

/* Challenge / table rows --------------------------------------------------- */

export const TableRowSkeleton = ({ cols = 8 }: { cols?: number }) => (
  <tr className="border-b border-border/40">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="p-4"><Skeleton className="h-5 w-full max-w-[120px]" /></td>
    ))}
  </tr>
);

export const TableSkeleton = ({ rows = 6, cols = 8 }: { rows?: number; cols?: number }) => (
  <div className="rounded-xl border border-border/40 overflow-hidden">
    <table className="w-full">
      <tbody>
        {Array.from({ length: rows }).map((_, i) => <TableRowSkeleton key={i} cols={cols} />)}
      </tbody>
    </table>
  </div>
);

/* Blog --------------------------------------------------------------------- */

export const BlogCardSkeleton = () => (
  <div className="rounded-2xl overflow-hidden border border-border/40 bg-card/40">
    <Skeleton className="h-56 w-full rounded-none" />
    <div className="p-6 space-y-3">
      <div className="flex gap-2">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-20 rounded-full" />
      </div>
      <Skeleton className="h-7 w-5/6" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  </div>
);

export const BlogPostSkeleton = () => (
  <div className="max-w-3xl mx-auto px-4 py-12 space-y-6">
    <Skeleton className="h-10 w-3/4" />
    <div className="flex gap-3"><Skeleton className="h-5 w-32" /><Skeleton className="h-5 w-24" /></div>
    <Skeleton className="h-72 w-full rounded-2xl" />
    <div className="space-y-3">
      {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-4 w-full" />)}
    </div>
  </div>
);

/* Firm details page header skeleton --------------------------------------- */

export const FirmDetailsSkeleton = () => (
  <div className="min-h-screen bg-background pt-20">
    <div className="container mx-auto px-4 py-8 space-y-8">
      <div className="flex items-center gap-4">
        <Skeleton className="w-20 h-20 rounded-2xl" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-11 w-32 rounded-xl" />
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
      <Skeleton className="h-10 w-full max-w-md rounded-lg" />
      <div className="grid md:grid-cols-2 gap-6">
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  </div>
);

/* Stat tiles (dashboard) --------------------------------------------------- */

export const StatTileSkeleton = () => (
  <div className="rounded-xl border border-border/40 bg-card/40 p-5 space-y-3">
    <Skeleton className="h-4 w-24" />
    <Skeleton className="h-8 w-32" />
    <Skeleton className="h-3 w-20" />
  </div>
);

export const StatGridSkeleton = ({ count = 4 }: { count?: number }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {Array.from({ length: count }).map((_, i) => <StatTileSkeleton key={i} />)}
  </div>
);

/* Review item -------------------------------------------------------------- */

export const ReviewSkeleton = () => (
  <div className="rounded-xl border border-border/40 bg-card/40 p-5 space-y-3">
    <div className="flex items-center gap-3">
      <Skeleton className="w-10 h-10 rounded-full" />
      <div className="space-y-2 flex-1">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="h-5 w-24" />
    </div>
    <Skeleton className="h-4 w-3/4" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-5/6" />
  </div>
);

export const ReviewListSkeleton = ({ count = 4 }: { count?: number }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, i) => <ReviewSkeleton key={i} />)}
  </div>
);
